import { ErrorSource } from 'config/constants/errors';
import { logError } from 'lib/observability';
import { PSPDFKitFacade } from 'lib/pdf/pspdfkit';
import { createNonPersistentStore } from 'lib/zustand/store';
import { logAnalyticsEventOnBackwardClick, logAnalyticsEventOnForwardClick } from 'modules/analytics/impl/playbackAnalytics';
import { askAiStoreActions } from 'modules/ask-ai/store';
import { useLibraryStore } from 'modules/library/stores/libraryStore';
import { PremiumUserRequirementNotMetError } from 'modules/listening/constants/listeningErrors';
import { listeningSettingsStoreActions } from 'modules/listening/features/settings/settingsStore';
import { MeasurementKey } from 'modules/profiling/measurementTypes';
import { profilingStoreActions } from 'modules/profiling/profilingStore';
import { ListenableContent, PlaybackInfo, SDKContentBundlerOptionsFacade, forceServerLocalStorageKey } from 'modules/sdk/lib';
import { isItemListenableContent, listenableContentErrorContext } from 'modules/sdk/lib/facade/listenableContent/utils';
import { ListeningDependencies, ListeningDependenciesFactory } from 'modules/sdk/listeningDependencies';
import { tryQueueMobileUpsellBanner } from 'modules/sideBanner/stores/continuePhoneActions';
import { subscriptionStoreActions } from 'modules/subscription/stores/actions';
import { subscriptionStoreSelectors } from 'modules/subscription/stores/selectors';
import { voiceStoreActions } from 'modules/voices/stores/actions';
import { checkAutoplayIsAvailable, shouldAutoplay } from 'utils/audio';

type BasePlaybackState = {
  latestPlaybackState: undefined;
  currentListenableContent: undefined;
  // @deprecated use listeningDependencies instead
  currentPlaybackInfo: undefined;
  // @deprecated use listeningDependencies instead
  currentOverlayInfo: undefined;
  // @deprecated use listeningDependencies instead
  contentBundlerOptionsFacade: undefined;
  listeningDependencies: undefined;
  currentPspdfKitFacade: undefined;
};

type InitialState = BasePlaybackState & {
  isReady: false;
};

type LoadingState = BasePlaybackState & {
  isReady: false;
};

export type PlaybackStoreReadyState = {
  isReady: true;

  currentListenableContent: ListenableContent;
  listeningDependencies: ListeningDependencies;
  // @deprecated use listeningDependencies instead
  currentPlaybackInfo: ListeningDependencies['playbackInfo'];
  // @deprecated use listeningDependencies instead
  contentBundlerOptionsFacade: SDKContentBundlerOptionsFacade;
  latestPlaybackState: PlaybackInfo['latestState'];
  currentPspdfKitFacade: PSPDFKitFacade;
};

export type PlaybackStoreState = InitialState | LoadingState | PlaybackStoreReadyState;

export const usePlaybackStore = createNonPersistentStore<PlaybackStoreState>(
  () => {
    return {
      isReady: false,
      currentPlaybackInfo: undefined,
      currentOverlayInfo: undefined,
      latestPlaybackState: undefined,
      currentListenableContent: undefined,
      contentBundlerOptionsFacade: undefined,
      listeningDependencies: undefined,
      currentPspdfKitFacade: undefined
    };
  },
  {
    isListeningScreenStore: true
  }
);

usePlaybackStore.subscribe((state, prevState) => {
  if (state.currentPlaybackInfo !== prevState.currentPlaybackInfo) {
    if (prevState.currentPlaybackInfo) {
      prevState.currentPlaybackInfo.controls.pause();
    }

    if (prevState.listeningDependencies) {
      prevState.listeningDependencies.destroy();
    }

    if (state.currentPlaybackInfo) {
      // note: it's safe to not clean this up because the listener will be removed when the PlaybackInfo is destroyed, see PlaybackInfo.destroy() implementation
      state.currentPlaybackInfo.addStateListener(playbackState => {
        usePlaybackStore.setState(() => ({
          latestPlaybackState: playbackState
        }));
      });
    }
  }
});

const _maybeStartAutoPlay = async (listeningDependencies: ListeningDependencies) => {
  const autoplay = shouldAutoplay();
  if (autoplay) {
    const isPlaying = listeningDependencies.playbackInfo.latestState.isPlaying;
    if (!isPlaying) {
      const isAutoplayAvailable = await checkAutoplayIsAvailable();
      if (isAutoplayAvailable) {
        resume();
      }
    }
  }
};

const readerBasedInit = async (listenableContent: ListenableContent, listeningDependencies: ListeningDependencies) => {
  usePlaybackStore.setState(() => ({
    currentListenableContent: listenableContent,
    listeningDependencies,
    currentPlaybackInfo: listeningDependencies.playbackInfo,
    contentBundlerOptionsFacade: listeningDependencies.contentBundlerOptionsFacade
  }));

  voiceStoreActions.initializeVoiceStore(listeningDependencies);
  await Promise.all([
    //
    listeningSettingsStoreActions.initializeListeningSettingsStore(),
    askAiStoreActions.initializeAskAiStore(),
    useLibraryStore.waitForInitialHydration()
  ]);
  subscriptionStoreActions.startWordTracker(listeningDependencies);

  usePlaybackStore.setState(() => ({
    isReady: true
  }));

  // run on the next tick once the UI is updated
  setTimeout(() => {
    _maybeStartAutoPlay(listeningDependencies);
  }, 1);

  // mobile app upsell banner
  tryQueueMobileUpsellBanner();

  ListeningDependenciesFactory.singleton.makeAvailableOffline(listenableContent);
};

const initializeReaderBasedBookExperience = async (listenableContent: ListenableContent): Promise<ListeningDependencies> => {
  const state = usePlaybackStore.getState();
  const currentListenableContent = state.currentListenableContent;
  if (currentListenableContent === listenableContent) {
    return state.listeningDependencies;
  }
  try {
    return profilingStoreActions.measureAction(
      async () => {
        usePlaybackStore.setState(() => ({
          isReady: false
        }));

        profilingStoreActions.startMeasurement({ measurementKey: MeasurementKey.sdkSetup, identifier: {}, context: {} });

        const listeningDependencies = await ListeningDependenciesFactory.singleton.create({
          listenableContent
        });

        await profilingStoreActions.measureAction(() => readerBasedInit(listenableContent, listeningDependencies), {
          measurementKey: MeasurementKey.readerBasedInit,
          identifier: {},
          context: {}
        });
        profilingStoreActions.endMeasurement({ measurementKey: MeasurementKey.instantListeningBookReaderReady, identifier: {}, context: {} });
        profilingStoreActions.measureFromNavigationStart({
          measurementKey: MeasurementKey.bookReaderReady,
          identifier: {},
          context: {}
        });

        return listeningDependencies;
      },
      { measurementKey: MeasurementKey.initializeListeningDependenciesForBookReader, identifier: {}, context: {} }
    );
  } catch (errorMessage) {
    const error = errorMessage instanceof Error ? errorMessage : new Error(`${errorMessage}`);

    // Handle expected premium requirement interruptions without logging
    if (error instanceof PremiumUserRequirementNotMetError) {
      usePlaybackStore.setState(() => ({
        isReady: false
      }));
      throw error;
    }

    logError(error, ErrorSource.PLAYBACK, {
      context: listenableContentErrorContext(listenableContent)
    });

    // If there is an ConnectionError it means that there is issue with cache heuristics and we tried to use cache even when the item is not available locally
    // It's not trivial to properly reload the item, so we just reload the page. We would need to reinitialize almost everything anyway.
    // Hopefully we will fix this soon, once SDK improves offline support so that they can do the fallback to server automatically.
    // This makes sense to do only if the item is loaded from the library (ie. it's itemListenableContent)
    if (error.message.startsWith('ConnectionError') && isItemListenableContent(listenableContent)) {
      localStorage.setItem(forceServerLocalStorageKey, 'true');
      window.location.reload();
    }

    usePlaybackStore.setState(() => ({
      isReady: false
    }));

    throw error;
  }
};

const initializeEpubExperience = async (listenableContent: ListenableContent): Promise<ListeningDependencies> => {
  const currentState = usePlaybackStore.getState();

  if (currentState.currentListenableContent === listenableContent) {
    return currentState.listeningDependencies;
  }

  try {
    usePlaybackStore.setState(() => ({
      isReady: false
    }));

    profilingStoreActions.startMeasurement({ measurementKey: MeasurementKey.sdkSetup, identifier: {}, context: {} });

    const listeningDependencies = await ListeningDependenciesFactory.singleton.create({
      listenableContent
    });

    await readerBasedInit(listenableContent, listeningDependencies);

    profilingStoreActions.endMeasurement({ measurementKey: MeasurementKey.instantListeningBookReaderReady, identifier: {}, context: {} });
    profilingStoreActions.measureFromNavigationStart({
      measurementKey: MeasurementKey.bookReaderReady,
      identifier: {},
      context: {}
    });

    return listeningDependencies;
  } catch (errorMessage) {
    const error = errorMessage instanceof Error ? errorMessage : new Error(`${errorMessage}`);

    logError(error, ErrorSource.PLAYBACK, {
      context: listenableContentErrorContext(listenableContent)
    });

    // If there is an ConnectionError it means that there is issue with cache heuristics and we tried to use cache even when the item is not available locally
    // It's not trivial to properly reload the item, so we just reload the page. We would need to reinitialize almost everything anyway.
    // Hopefully we will fix this soon, once SDK improves offline support so that they can do the fallback to server automatically.
    // This makes sense to do only if the item is loaded from the library (ie. it's itemListenableContent)
    if (error.message.startsWith('ConnectionError') && isItemListenableContent(listenableContent)) {
      localStorage.setItem(forceServerLocalStorageKey, 'true');
      window.location.reload();
    }

    usePlaybackStore.setState(() => ({
      isReady: false
    }));

    throw error;
  }
};

const initializeClassicExperience = async ({ listenableContent }: { listenableContent: ListenableContent }): Promise<ListeningDependencies> => {
  const currentState = usePlaybackStore.getState();
  if (currentState.currentListenableContent === listenableContent) {
    return currentState.listeningDependencies;
  }
  try {
    const listeningDependencies = await ListeningDependenciesFactory.singleton.create({
      listenableContent
    });

    await readerBasedInit(listenableContent, listeningDependencies);
    profilingStoreActions.endMeasurement({ measurementKey: MeasurementKey.instantListeningClassicReaderReady, identifier: {}, context: {} });
    profilingStoreActions.measureFromNavigationStart({ measurementKey: MeasurementKey.classicReaderReady, identifier: {}, context: {} });

    return listeningDependencies;
  } catch (errorMessage) {
    const error = errorMessage instanceof Error ? errorMessage : new Error(`${errorMessage}`);
    logError(error, ErrorSource.PLAYBACK, {
      context: listenableContentErrorContext(listenableContent)
    });

    usePlaybackStore.setState(() => ({
      isReady: false
    }));

    throw error;
  }
};

const _withShouldSwitchToFreeCheck = (fn: (playbackInfo: PlaybackInfo) => void) => {
  return (playbackInfo?: PlaybackInfo) => {
    playbackInfo ??= usePlaybackStore.getState().currentPlaybackInfo;
    if (!playbackInfo) {
      return;
    }

    if (subscriptionStoreSelectors.getShouldSwitchToFree()) {
      subscriptionStoreActions.switchToFree();
    }

    fn(playbackInfo);
  };
};

const pressPlayPause = _withShouldSwitchToFreeCheck((playbackInfo: PlaybackInfo) => {
  playbackInfo.controls.pressPlayPause();
});

const pause = _withShouldSwitchToFreeCheck((playbackInfo: PlaybackInfo) => {
  playbackInfo.pause();
});

const resume = _withShouldSwitchToFreeCheck((playbackInfo: PlaybackInfo) => {
  playbackInfo.resume();
});

const onForwardClick = (playbackInfo?: PlaybackInfo) => {
  playbackInfo ??= usePlaybackStore.getState().currentPlaybackInfo;
  if (!playbackInfo) return;

  playbackInfo.controls.skipForwards();
  logAnalyticsEventOnForwardClick();
};

const onBackwardClick = (playbackInfo?: PlaybackInfo) => {
  playbackInfo ??= usePlaybackStore.getState().currentPlaybackInfo;
  if (!playbackInfo) return;

  playbackInfo.controls.skipBackwards();
  logAnalyticsEventOnBackwardClick();
};

const cleanup = () => {
  const currentState = usePlaybackStore.getState();
  if (currentState.listeningDependencies) {
    currentState.listeningDependencies.playbackInfo.pause();
    currentState.listeningDependencies.destroy();
  }
};

export const playbackStoreActions = {
  cleanup,
  initializeClassicExperience,
  initializeEpubExperience,
  initializeReaderBasedBookExperience,
  onBackwardClick,
  onForwardClick,
  pressPlayPause,
  pause,
  resume
};
